import React, { createContext, useContext, useState, useCallback } from "react";
import type {
  NavigationItem,
  NavigationHistoryContextType,
  NavigationHistoryProviderProps,
} from "@/types/contexts/navigationHistory";

const NavigationHistoryContext = createContext<NavigationHistoryContextType | null>(null);

const MAX_HISTORY_SIZE = 10;

export function NavigationHistoryProvider({ children }: NavigationHistoryProviderProps) {
  const [navHistory, setNavHistory] = useState<NavigationItem[]>([]);

  const pushNavigation = useCallback((item: NavigationItem) => {
    // Validate item before adding to history - reject undefined/invalid values
    if (!item.panel || item.panel === "undefined" || !item.id || item.id === "undefined") {
      return;
    }

    setNavHistory((prev) => {
      // Don't push if navigating to the same entity
      const lastItem = prev[prev.length - 1];
      if (lastItem && lastItem.panel === item.panel && lastItem.id === item.id) {
        return prev;
      }

      // Limit history size
      const newHistory = [...prev, item];
      if (newHistory.length > MAX_HISTORY_SIZE) {
        return newHistory.slice(1);
      }
      return newHistory;
    });
  }, []);

  const popNavigation = useCallback(() => {
    let poppedItem: NavigationItem | undefined;
    setNavHistory((prev) => {
      if (prev.length === 0) return prev;
      poppedItem = prev[prev.length - 1];
      return prev.slice(0, -1);
    });
    return poppedItem;
  }, []);

  const clearHistory = useCallback(() => {
    setNavHistory([]);
  }, []);

  const contextValue: NavigationHistoryContextType = {
    navHistory,
    pushNavigation,
    popNavigation,
    clearHistory,
  };

  return <NavigationHistoryContext.Provider value={contextValue}>{children}</NavigationHistoryContext.Provider>;
}

export function useNavigationHistory(): NavigationHistoryContextType {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error("useNavigationHistory must be used within a NavigationHistoryProvider");
  }
  return context;
}
